
import { CampaignFormData, WorkflowStep } from '../types';
import { callGemini } from './gemini';

interface ReportSection {
  title: string;
  content: string;
}

interface ConsolidatedReport {
  title: string;
  executiveSummary: string;
  sections: ReportSection[];
  recommendations: string;
  nextSteps: string;
}

export async function generateConsolidatedReport(
  formData: CampaignFormData,
  steps: WorkflowStep[],
  apiKey: string
): Promise<ConsolidatedReport> {
  try {
    // Generate executive summary prompt
    const executiveSummaryPrompt = `
Generate a concise executive summary for the corporate campaign analysis of ${formData.core_issue_details.campaign_name} targeting ${formData.core_issue_details.target_company}.

Context:
- Labor Issue: ${formData.core_issue_details.labor_issue}
- Target Company: ${formData.core_issue_details.target_company}
- Primary Goal: ${formData.campaign_outcome.primary_goal}
- Target Audience: ${formData.campaign_outcome.target_audience}

Include:
1. Brief overview of the campaign and labor issue
2. Key findings about corporate vulnerabilities and leverage points
3. Critical success factors for the campaign
4. Major risks and challenges from corporate opposition
5. Primary strategic recommendations

Format the response as a professional executive summary of 2-3 paragraphs.`;

    // Generate recommendations prompt
    const recommendationsPrompt = `
Based on the complete analysis of the ${formData.core_issue_details.campaign_name} corporate campaign against ${formData.core_issue_details.target_company}, generate strategic recommendations.

Analysis Context:
${steps.map(step => `- ${step.title}: ${step.result}`).join('\n')}

Provide:
1. Top 3-5 strategic priorities for corporate pressure
2. Specific tactical recommendations for worker organizing and external pressure
3. Risk mitigation strategies for corporate retaliation
4. Success metrics and campaign evaluation criteria

Format as clear, actionable recommendations with bullet points.`;

    // Generate next steps prompt
    const nextStepsPrompt = `
Create a detailed implementation plan for the ${formData.core_issue_details.campaign_name} corporate campaign.

Campaign Goal: ${formData.campaign_outcome.primary_goal}
Target Audience: ${formData.campaign_outcome.target_audience}
Target Company: ${formData.core_issue_details.target_company}

Include:
1. Immediate actions (next 30 days) - worker organizing and research
2. Medium-term priorities (60-90 days) - escalation and pressure tactics
3. Long-term strategic initiatives - sustained campaign operations
4. Key milestones and decision points
5. Resource requirements and capacity building needs

Format as a clear timeline with specific actions, responsible parties, and deadlines.`;

    // Execute all prompts in parallel
    const [executiveSummary, recommendations, nextSteps] = await Promise.all([
      callGemini(executiveSummaryPrompt, apiKey),
      callGemini(recommendationsPrompt, apiKey),
      callGemini(nextStepsPrompt, apiKey)
    ]);

    // Validate responses
    if (executiveSummary.error || recommendations.error || nextSteps.error) {
      throw new Error('Failed to generate one or more report sections');
    }

    // Compile report sections
    const sections: ReportSection[] = steps.map(step => ({
      title: step.title,
      content: step.result || 'Analysis pending'
    }));

    return {
      title: `${formData.core_issue_details.campaign_name} - Corporate Campaign Analysis Report`,
      executiveSummary: executiveSummary.content,
      sections,
      recommendations: recommendations.content,
      nextSteps: nextSteps.content
    };
  } catch (error) {
    console.error('Error generating consolidated report:', error);
    throw error;
  }
}

export function formatConsolidatedReport(report: ConsolidatedReport): string {
  return `# ${report.title}
Date: ${new Date().toLocaleDateString()}

## Executive Summary

${report.executiveSummary}

## Table of Contents

1. Executive Summary
2. Campaign Analysis Reports
3. Strategic Recommendations
4. Implementation Plan & Next Steps

## Campaign Analysis Reports

${report.sections.map((section, index) => `
### ${index + 1}. ${section.title}

${section.content}
`).join('\n')}

## Strategic Recommendations

${report.recommendations}

## Implementation Plan & Next Steps

${report.nextSteps}
`;
}
